import {
  MaterialComponentCategory,
  SiteMaterialDetail,
  SiteMaterialList,
  useApiTradeV1SiteMaterialsRetrieve,
} from 'api';
import { MaterialSupportedComponentList } from 'features';
import {
  AllowedMaterialSpecAutocomplete,
  BooleanSelectField,
  MaterialGradeAutocomplete,
  MaterialOriginAutocomplete,
  SiteMaterialWithProductAutocomplete,
  TextField,
  useFormContext,
} from 'form';
import { useTranslation } from 'i18n';
import { useContext } from 'react';
import { Box, Button, Divider, Grid, Stack, Typography } from 'ui';

import { MaterialDirtLevel } from '../../../material';
import { getCreateSiteMaterialUrl, NoSiteMaterialsOption } from '../../../site-materials';
import { PurchaseOrderFormContext } from '../../contexts/PurchaseOrderFormContext';
import { BasePurchaseOrderFormValues } from '../../types';
import { IsInternalGradeShownToSeller } from '../IsInternalGradeShownToSeller/IsInternalGradeShownToSeller';
import { isMaterialProductSyncedWithMaterialProps } from './isMaterialProductSyncedWithMaterialProps';
import { updateMaterialProperties } from './updateMaterialProperties';

export const SellerMaterialDetailsSection = () => {
  const { t } = useTranslation('orders');
  const { setValue, watch, getValues } = useFormContext<BasePurchaseOrderFormValues>();
  const values = getValues();
  const { variant } = useContext(PurchaseOrderFormContext);

  const producerId = watch('producer_id');
  const siteMaterialId = watch('site_material_id') || '';
  const { data: siteMaterialDetail } = useApiTradeV1SiteMaterialsRetrieve(siteMaterialId?.toString());
  const materialId = watch('material_id');
  const materialColourId = watch('material_colour_id');

  const onSyncMaterial = (siteMaterialDetail: SiteMaterialDetail | SiteMaterialList | undefined) => {
    setValue('should_update_material_properties_from_site_material', true);
    if (!siteMaterialDetail) {
      return;
    }
    updateMaterialProperties(siteMaterialDetail, setValue);
  };

  return (
    <Box>
      <Typography variant="h6" fontWeight="bold">
        {t('buyerMaterialDetailsSection.title')}
      </Typography>
      <Grid
        container
        spacing={2}
        sx={{
          marginBottom: 2,
          marginTop: 0,
        }}
      >
        <Grid
          size={{
            md: 8,
            xs: 12,
          }}
        >
          <Stack
            direction="column"
            sx={{
              justifyContent: 'center',
              alignItems: 'flex-end',
            }}
          >
            <Button
              variant="text"
              component="a"
              disabled={!producerId}
              target="_blank"
              href={getCreateSiteMaterialUrl(producerId)}
              aria-label={t('sellerMaterialDetailsSection.actions.addNewSiteMaterialAriaLabel')}
            >
              {t('sellerMaterialDetailsSection.actions.addNewSiteMaterial')}
            </Button>
            <SiteMaterialWithProductAutocomplete
              label={t('buyerMaterialDetailsSection.labels.siteMaterial')}
              name="site_material_id"
              tradeCompanyId={producerId}
              onSelectOption={(siteMaterial) => onSyncMaterial(siteMaterial)}
              noOptionsText={<NoSiteMaterialsOption tradeCompanyId={producerId} />}
            />
          </Stack>
        </Grid>
        <Grid
          size={{
            md: 4,
            xs: 12,
          }}
        >
          {variant === 'update' && (
            <Button
              disabled={isMaterialProductSyncedWithMaterialProps(values, siteMaterialDetail)}
              onClick={() => onSyncMaterial(siteMaterialDetail)}
            >
              {t('buyerMaterialDetailsSection.actions.syncMaterialProperties')}
            </Button>
          )}
        </Grid>
        {siteMaterialDetail ? (
          <Grid
            size={{
              md: 12,
              xs: 12,
            }}
          >
            <IsInternalGradeShownToSeller siteMaterialDetail={siteMaterialDetail} />
          </Grid>
        ) : null}
        <Grid
          size={{
            md: 4,
            xs: 12,
          }}
        >
          <AllowedMaterialSpecAutocomplete
            name="material_spec_id"
            label={t('buyerMaterialDetailsSection.labels.material')}
            disabled
          />
        </Grid>
        <Grid
          size={{
            md: 4,
            xs: 12,
          }}
        >
          <MaterialGradeAutocomplete
            label={t('buyerMaterialDetailsSection.labels.grade')}
            name="grade_id"
            disabled
            infoTooltip={t('buyerMaterialDetailsSection.tooltips.grade')}
          />
        </Grid>

        <Grid
          size={{
            md: 4,
            xs: 12,
          }}
        >
          <MaterialOriginAutocomplete
            label={t('buyerMaterialDetailsSection.labels.origin')}
            name="origin_id"
            infoTooltip={t('buyerMaterialDetailsSection.tooltips.origin')}
            disabled
          />
        </Grid>
        <Grid
          size={{
            md: 4,
            xs: 12,
          }}
        >
          <MaterialDirtLevel
            label={t('buyerMaterialDetailsSection.labels.dirtLevel')}
            name="dirt_level"
            infoTooltip={t('buyerMaterialDetailsSection.tooltips.dirtLevel')}
          />
        </Grid>
        <Grid
          size={{
            md: 4,
            xs: 12,
          }}
        >
          <BooleanSelectField
            label={t('buyerMaterialDetailsSection.labels.foodGrade')}
            name="is_food_grade"
            infoTooltip={t('buyerMaterialDetailsSection.tooltips.foodGrade')}
            disabled
          />
        </Grid>
        <Grid
          size={{
            md: 4,
            xs: 12,
          }}
        >
          <BooleanSelectField
            label={t('buyerMaterialDetailsSection.labels.containsCapsAndLabels')}
            name="has_caps_and_labels"
            withNotAvailable
            infoTooltip={t('buyerMaterialDetailsSection.tooltips.containsCapsAndLabels')}
            disabled
          />
        </Grid>
        <Grid
          size={{
            md: 4,
            xs: 12,
          }}
        >
          <TextField
            label={t('buyerMaterialDetailsSection.labels.baselCodeOverride')}
            name="basel_code_override"
            slotProps={{
              htmlInput: {
                maxLength: 8,
              },
            }}
            infoTooltip={t('buyerMaterialDetailsSection.tooltips.baselCodeOverride')}
          />
        </Grid>
      </Grid>
      <MaterialSupportedComponentList
        name="seller_material_component_values"
        materialId={materialId}
        materialColourId={materialColourId}
        title={t('buyerMaterialDetailsSection.supportedComponents.sellerTitle')}
        category={MaterialComponentCategory.MATERIAL_QUALITY}
        disabled
      />
      <Divider />
    </Box>
  );
};
